import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { O2dTableColumn } from '../types/table-column.type.js';
import type { O2dTableSort } from '../types/table-sort.type.js';
import { resolveSortAccessor, readAccessorValue } from './column-accessor.js';

/**
 * Reactive controller that provides the table's single active sort.
 * Rows are ordered by the active column's `sortValue` accessor, falling back to
 * `filterValue` (mirroring the `O2dTableColumn` default). Pure/synchronous, no RxJS.
 */
export class TableSortController implements ReactiveController {
    constructor(host: ReactiveControllerHost) {
        host.addController(this);
    }

    /**
     * No teardown required — this controller holds no timers or subscriptions.
     * Present to satisfy the `ReactiveController` contract (popover-list precedent).
     */
    hostDisconnected(): void {
        // intentionally empty: stateless controller, nothing to tear down
    }

    /**
     * Returns a NEW array of rows ordered by the active column. `undefined`
     * `activeSort` (or an unknown column) returns the rows unchanged. The sort is
     * stable: rows with equal keys keep their original relative order.
     */
    sort(rows: unknown[], activeSort: O2dTableSort | undefined, columns: O2dTableColumn[]): unknown[] {
        if (!activeSort) {
            return rows;
        }

        const column = columns.find(candidate => candidate.id === activeSort.columnId);
        if (!column) {
            return rows;
        }

        const accessor = resolveSortAccessor(column);
        const factor = activeSort.direction === 'desc' ? -1 : 1;

        return [...rows].sort((first, second) => {
            const a = readAccessorValue(accessor, first);
            const b = readAccessorValue(accessor, second);
            if (a < b) return -1 * factor;
            if (a > b) return 1 * factor;
            return 0;
        });
    }

    /**
     * Header-click helper: computes the next sort. New column → ascending; same
     * column flips asc↔desc (no third "unsorted" state, per the PRD decision).
     */
    toggle(columnId: string, currentSort?: O2dTableSort): O2dTableSort {
        if (currentSort?.columnId === columnId && currentSort.direction === 'asc') {
            return { columnId, direction: 'desc' };
        }
        return { columnId, direction: 'asc' };
    }
}
